import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import {
  Shield,
  LayoutDashboard,
  MessageSquare,
  Activity,
  Bell,
  Search,
  Moon,
  Sun,
  Send,
  Trash2,
  Clock,
  AlertTriangle,
  ChevronRight,
  Sparkles,
  X,
  Zap,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";

import "./App.css";

const API_BASE_URL =
  "https://1wls2elsr0.execute-api.us-east-1.amazonaws.com/prod";

const PIE_COLORS = ["#6366F1", "#EC4899", "#F59E0B", "#10B981", "#06B6D4"];

function App() {
  const [activeMenu, setActiveMenu] = useState("landing");
  const [darkMode, setDarkMode] = useState(true);

  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({});
  const [graphData, setGraphData] = useState([]);
  const [pieData, setPieData] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");

  const [chatOpen, setChatOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [chatMessages, setChatMessages] = useState([
    {
      sender: "ai",
      text: "👋 Welcome to AI SOC. Ask me anything about your security incidents or cloud posture.",
    },
  ]);

  const [showNotifications, setShowNotifications] = useState(false);
  const [analyzedIncidents, setAnalyzedIncidents] = useState(new Set());

  const floatingChatRef = useRef(null);
  const notifRef = useRef(null);

  // ── FETCH INCIDENTS ──────────────────────────────────────────
  const fetchIncidents = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/incidents`);

      const data =
        typeof response.data.body === "string"
          ? JSON.parse(response.data.body)
          : response.data;

      const deletedIds = JSON.parse(
        localStorage.getItem("soc_deleted_ids") || "[]"
      );

      const formatted = (data.incidents || [])
        .map((item, index) => ({
          ...item,
          id: item.incident_id || `inc_${index}`,
        }))
        .filter((item) => !deletedIds.includes(item.id));

      setIncidents(formatted);

      // Recalculate stats from filtered list
      setStats({
        total: formatted.length,
        critical: formatted.filter(
          (i) => i.severity?.toUpperCase() === "CRITICAL"
        ).length,
        high: formatted.filter(
          (i) => i.severity?.toUpperCase() === "HIGH"
        ).length,
        auto_actioned: formatted.filter(
          (i) => i.action_taken && i.action_taken.toUpperCase() !== "NONE"
        ).length,
      });

      setGraphData(data.graphData || []);
      setPieData(data.pieData || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchIncidents();
    const interval = setInterval(fetchIncidents, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    floatingChatRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ── FILTERS ──────────────────────────────────────────────────
  const filteredIncidents = useMemo(() => {
    return incidents.filter((item) => {
      const sev = item.severity?.toUpperCase() || "";
      if (activeFilter === "CRITICAL" && sev !== "CRITICAL") return false;
      if (activeFilter === "HIGH" && sev !== "HIGH") return false;
      if (
        activeFilter === "AUTO_ACTIONED" &&
        (!item.action_taken || item.action_taken === "NONE")
      )
        return false;

      const s = searchTerm.toLowerCase();
      return (
        item.alert_type?.toLowerCase().includes(s) ||
        item.status?.toLowerCase().includes(s) ||
        item.action_taken?.toLowerCase().includes(s) ||
        item.severity?.toLowerCase().includes(s)
      );
    });
  }, [incidents, searchTerm, activeFilter]);

  const unanalyzedCount = Math.max(
    0,
    incidents.length - analyzedIncidents.size
  );

  // ── DELETE INCIDENT ──────────────────────────────────────────
  const deleteIncident = (id) => {
    // Persist to localStorage
    const existing = JSON.parse(
      localStorage.getItem("soc_deleted_ids") || "[]"
    );
    if (!existing.includes(id)) {
      localStorage.setItem(
        "soc_deleted_ids",
        JSON.stringify([...existing, id])
      );
    }

    const updated = incidents.filter((item) => item.id !== id);
    setIncidents(updated);

    // Update stats live
    setStats({
      total: updated.length,
      critical: updated.filter(
        (i) => i.severity?.toUpperCase() === "CRITICAL"
      ).length,
      high: updated.filter(
        (i) => i.severity?.toUpperCase() === "HIGH"
      ).length,
      auto_actioned: updated.filter(
        (i) => i.action_taken && i.action_taken.toUpperCase() !== "NONE"
      ).length,
    });
  };

  // ── AI CHAT ──────────────────────────────────────────────────
  const sendMessage = async (customMessage = null) => {
    const finalMessage = customMessage || message;
    if (!finalMessage.trim()) return;

    setChatMessages((prev) => [...prev, { sender: "user", text: finalMessage }]);
    setMessage("");
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/chat`, {
        message: finalMessage,
      });

      let aiText = "No response received.";

      if (response.data.body) {
        const parsed =
          typeof response.data.body === "string"
            ? JSON.parse(response.data.body)
            : response.data.body;
        aiText = parsed.response || parsed.answer || aiText;
      } else {
        aiText = response.data.response || response.data.answer || aiText;
      }

      setChatMessages((prev) => [...prev, { sender: "ai", text: aiText }]);
    } catch {
      setChatMessages((prev) => [
        ...prev,
        { sender: "ai", text: "Unable to connect to AI assistant." },
      ]);
    }

    setLoading(false);
  };

  const analyzeIncident = (incident) => {
    setActiveMenu("chat");
    setChatOpen(true);
    setAnalyzedIncidents((prev) => new Set(prev).add(incident.id));

    const prompt = `Analyze this AWS security incident:

Alert Type: ${incident.alert_type}
Severity: ${incident.severity}
Status: ${incident.status}
Action Taken: ${incident.action_taken}
Recommendation: ${incident.recommendation}
Reasoning: ${incident.agent_reasoning}

Explain: 1. What happened  2. Risk level  3. Impact  4. Remediation steps`;

    sendMessage(prompt);
  };

  // ── LANDING PAGE ─────────────────────────────────────────────
  if (activeMenu === "landing") {
    return (
      <div className="landing-page">
        <div className="landing-orb orb1" />
        <div className="landing-orb orb2" />

        <div className="landing-content">
          <div className="hero-badge">
            <Sparkles size={14} />
            AI-Powered Security Operations Center
          </div>

          <h1>
            Detect. Analyze. <span>Respond.</span>
          </h1>

          <p>
            Intelligent AWS threat monitoring, AI-powered incident analysis,
            automated remediation and real-time cloud security visibility.
          </p>

          <button
            className="hero-btn"
            onClick={() => setActiveMenu("dashboard")}
          >
            Launch Dashboard
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  // ── MAIN APP ─────────────────────────────────────────────────
  return (
    <div className={darkMode ? "app dark" : "app light"}>
      {/* SIDEBAR */}
      <div className="sidebar">
        <div>
          <div className="logo">
            <div className="logo-icon">
              <Shield size={22} />
            </div>
            <div>
              <h2>AI SOC</h2>
              <p>Security Center</p>
            </div>
          </div>

          <nav className="menu-list">
            <button
              className={`menu-btn ${activeMenu === "dashboard" ? "active" : ""}`}
              onClick={() => setActiveMenu("dashboard")}
            >
              <LayoutDashboard size={18} />
              <span>Dashboard</span>
            </button>

            <button
              className={`menu-btn ${activeMenu === "chat" ? "active" : ""}`}
              onClick={() => setActiveMenu("chat")}
            >
              <MessageSquare size={18} />
              <span>AI Chat</span>
            </button>

            <button
              className={`menu-btn ${activeMenu === "analytics" ? "active" : ""}`}
              onClick={() => setActiveMenu("analytics")}
            >
              <TrendingUp size={18} />
              <span>Analytics</span>
            </button>
          </nav>
        </div>

        <button className="theme-btn" onClick={() => setDarkMode(!darkMode)}>
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
        </button>
      </div>

      {/* MAIN */}
      <div className="main">
        {/* TOPBAR */}
        <div className="topbar">
          <div>
            <h1>Security Operations Dashboard</h1>
            <p>AI-powered cloud threat detection and response platform</p>
          </div>

          <div className="top-actions">
            <div className="search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search incidents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* NOTIFICATION BELL */}
            <div className="notification-wrapper" ref={notifRef}>
              <button
                className="icon-btn"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell size={18} />
                {unanalyzedCount > 0 && (
                  <span className="notification-badge">{unanalyzedCount}</span>
                )}
              </button>

              {showNotifications && (
                <div className="notification-dropdown">
                  <div className="notif-header">
                    <h4>Notifications</h4>
                    <button
                      className="notif-close"
                      onClick={() => setShowNotifications(false)}
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {unanalyzedCount > 0 ? (
                    <div className="notif-body">
                      <div className="notif-count-block">
                        <AlertTriangle size={20} className="notif-warn-icon" />
                        <div>
                          <p className="notif-count-title">
                            {unanalyzedCount} Unanalyzed Incident
                            {unanalyzedCount !== 1 ? "s" : ""}
                          </p>
                          <p className="notif-count-sub">
                            New threats require your attention
                          </p>
                        </div>
                      </div>
                      <button
                        className="notif-cta-btn"
                        onClick={() => {
                          setActiveMenu("dashboard");
                          setShowNotifications(false);
                        }}
                      >
                        <MessageSquare size={14} />
                        Analyze with AI
                      </button>
                    </div>
                  ) : (
                    <div className="notif-empty">
                      <CheckCircle2 size={32} className="notif-ok-icon" />
                      <p>All incidents analyzed!</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button className="icon-btn" onClick={() => setChatOpen(true)}>
              <MessageSquare size={18} />
            </button>
          </div>
        </div>

        {/* ── DASHBOARD ── */}
        {activeMenu === "dashboard" && (
          <>
            <div className="stats-grid">
              <div
                className={`card ${activeFilter === "ALL" ? "active-filter" : ""}`}
                onClick={() => setActiveFilter("ALL")}
              >
                <div className="card-icon total-icon">
                  <Shield size={20} />
                </div>
                <div>
                  <h4>Total Incidents</h4>
                  <h2>{stats.total || 0}</h2>
                </div>
              </div>

              <div
                className={`card critical-card ${activeFilter === "CRITICAL" ? "active-filter" : ""}`}
                onClick={() => setActiveFilter("CRITICAL")}
              >
                <div className="card-icon critical-icon">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h4>Critical</h4>
                  <h2>{stats.critical || 0}</h2>
                </div>
              </div>

              <div
                className={`card warning-card ${activeFilter === "HIGH" ? "active-filter" : ""}`}
                onClick={() => setActiveFilter("HIGH")}
              >
                <div className="card-icon warning-icon">
                  <Activity size={20} />
                </div>
                <div>
                  <h4>High Severity</h4>
                  <h2>{stats.high || 0}</h2>
                </div>
              </div>

              <div
                className={`card success-card ${activeFilter === "AUTO_ACTIONED" ? "active-filter" : ""}`}
                onClick={() => setActiveFilter("AUTO_ACTIONED")}
              >
                <div className="card-icon success-icon">
                  <Zap size={20} />
                </div>
                <div>
                  <h4>Auto Actioned</h4>
                  <h2>{stats.auto_actioned || 0}</h2>
                </div>
              </div>
            </div>

            {/* INCIDENTS TABLE */}
            <div className="panel">
              <div className="panel-title">
                <Shield size={20} />
                <h3>Security Incidents</h3>
                <span className="incident-count">{filteredIncidents.length}</span>
              </div>

              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Incident</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>Time</th>
                      <th>Action Taken</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIncidents.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="empty-state">
                          No incidents found.
                        </td>
                      </tr>
                    ) : (
                      filteredIncidents.map((item, index) => (
                        <tr key={item.id || index}>
                          <td>
                            <div className="incident-block">
                              <div className="incident-title">
                                {item.alert_type}
                              </div>
                              {item.agent_reasoning && (
                                <div className="incident-reason">
                                  {item.agent_reasoning}
                                </div>
                              )}
                              {item.recommendation && (
                                <div className="incident-recommendation">
                                  💡 {item.recommendation}
                                </div>
                              )}
                            </div>
                          </td>

                          <td>
                            <span
                              className={`severity ${item.severity?.toLowerCase()}`}
                            >
                              {item.severity}
                            </span>
                          </td>

                          <td>
                            <span className="status-text">{item.status}</span>
                          </td>

                          <td>
                            <div className="time-cell">
                              <Clock size={13} />
                              {item.timestamp
                                ? new Date(item.timestamp).toLocaleString()
                                : "N/A"}
                            </div>
                          </td>

                          <td>
                            <span className="action-text">
                              {item.action_taken}
                            </span>
                          </td>

                          <td>
                            <div className="row-actions">
                              <button
                                className="row-btn analyze-btn"
                                onClick={() => analyzeIncident(item)}
                                title="Analyze with AI"
                              >
                                <MessageSquare size={14} />
                              </button>
                              <button
                                className="row-btn delete-btn"
                                onClick={() => deleteIncident(item.id)}
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── ANALYTICS ── */}
        {activeMenu === "analytics" && (
          <>
            <div className="analytics-grid">
              <div className="analytics-card">
                <div className="analytics-icon">
                  <TrendingUp size={22} />
                </div>
                <h2>98%</h2>
                <p>Threat Detection Accuracy</p>
              </div>
              <div className="analytics-card">
                <div className="analytics-icon">
                  <Zap size={22} />
                </div>
                <h2>12s</h2>
                <p>Average Response Time</p>
              </div>
              <div className="analytics-card">
                <div className="analytics-icon">
                  <Shield size={22} />
                </div>
                <h2>24/7</h2>
                <p>AI Monitoring</p>
              </div>
            </div>

            <div className="charts-grid">
              <div className="panel">
                <div className="panel-title">
                  <Activity size={18} />
                  <h3>Threat Distribution</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      outerRadius={110}
                      innerRadius={55}
                      paddingAngle={3}
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "#131720",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "12px",
                        color: "#f1f5f9",
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="panel">
                <div className="panel-title">
                  <TrendingUp size={18} />
                  <h3>Weekly Threat Activity</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={graphData}>
                    <defs>
                      <linearGradient
                        id="areaGrad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#6366F1"
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="95%"
                          stopColor="#6366F1"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.05)"
                    />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: "#94a3b8", fontSize: 12 }}
                    />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        background: "#131720",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "12px",
                        color: "#f1f5f9",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="incidents"
                      stroke="#6366F1"
                      strokeWidth={2}
                      fill="url(#areaGrad)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="panel">
                <div className="panel-title">
                  <Activity size={18} />
                  <h3>Severity Breakdown</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={graphData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.05)"
                    />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: "#94a3b8", fontSize: 12 }}
                    />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        background: "#131720",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "12px",
                        color: "#f1f5f9",
                      }}
                    />
                    <Bar
                      dataKey="incidents"
                      fill="#6366F1"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* ── CHAT PAGE ── */}
        {activeMenu === "chat" && (
          <div className="panel chat-page">
            <div className="panel-title">
              <MessageSquare size={20} />
              <h3>AI Security Assistant</h3>
            </div>

            <div className="chat-body-page">
              {chatMessages.map((msg, index) => (
                <div key={index} className={`chat-msg ${msg.sender}`}>
                  {msg.text}
                </div>
              ))}
              {loading && (
                <div className="chat-msg ai typing">
                  <span /><span /><span />
                </div>
              )}
              <div ref={floatingChatRef} />
            </div>

            <div className="chat-input-area">
              <input
                type="text"
                placeholder="Ask AI about your incidents..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              />
              <button onClick={sendMessage}>
                <Send size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── FLOATING CHAT ── */}
      {chatOpen && (
        <div className="floating-chat">
          <div className="floating-header">
            <div className="floating-header-info">
              <div className="floating-avatar">
                <Sparkles size={16} />
              </div>
              <div>
                <h3>AI Assistant</h3>
                <p>Powered by AWS Bedrock</p>
              </div>
            </div>
            <button onClick={() => setChatOpen(false)}>
              <X size={18} />
            </button>
          </div>

          <div className="floating-body">
            {chatMessages.map((msg, index) => (
              <div key={index} className={`chat-msg ${msg.sender}`}>
                {msg.text}
              </div>
            ))}
            {loading && (
              <div className="chat-msg ai typing">
                <span /><span /><span />
              </div>
            )}
            <div ref={floatingChatRef} />
          </div>

          <div className="floating-input">
            <input
              type="text"
              placeholder="Ask AI..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            />
            <button onClick={sendMessage}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;